export interface Plan {
  id: string;
  name: string;
  description: string;
  productId: string;
  stripeProductId: string;
  employeeDevicesCount: number;
  devicesCount: 20;
  product: {
    id: string;
    name: string;
    isActive: boolean;
    period: number;
    trialDays: number;
  };
  prices: {
    id: string;
    stripePriceId: string;
    amount: string;
    currency: string;
    planId: string;
    isActive: boolean;
  }[];
}

export type Tier = {
  min: number;
  max: number;
  amount: number;
};

export interface TPaymentMethod {
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  stripePaymentMethodId: string;
  isDefault: boolean;
  type: string;
  companyId: string;
  card: {
    id: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    brand: string;
    expMonth: number;
    expYear: number;
    funding: string;
    last4: string;
    cvcCheck: string;
    name: string;
    paymentMethodId: string;
  };
}

export type Price = {
  id: string;
  stripePriceId: string;
  currency: string;
  tiers: Tier[];
  type: "DEVICE";
  period: PeriodsTypeEnums;
};

export enum PeriodsTypeEnums {
  TRIENNIAL = "TRIENNIAL",
  ANNUAL = "ANNUAL",
  MONTHLY = "MONTHLY",
}

export enum PlansTypeEnums {
  DEVICE_MANAGEMENT = "DEVICE_MANAGEMENT",
  DEVICE_MANAGEMENT_AND_SECURITY = "DEVICE_MANAGEMENT_AND_SECURITY",
  PLATFORM = "PLATFORM",
}

export type TProduct = {
  id: string;
  name: string;
  bundle: PlansTypeEnums;
  trialDays: number;
  discountPercentage: number;
  prices: Price[];
  platform: EPlatform;
};

export enum EPlatform {
  MOBILE = "MOBILE",
  DESKTOP = "DESKTOP",
}

export enum SubscriptionStateEnum {
  ACTIVE = "active", //premium user (has access to all features)
  TRIALING = "trialing", //trial user (has access to all features but has a limit on the number of devices)
  INCOMPLETE = "incomplete", //premium user with incomplete payment (has access to all features)
  INCOMPLETE_EXPIRED = "incomplete_expired", //expired permium user (hasn't access)
  CANCELED = "canceled", //expired premium user (hasn't access)
  UNPAID = "unpaid", //expired premium user (hasn't access)
  PAST_DUE = "past_due", //premium user with incomplete payment (has access to all features)
  PAUSED = "paused", //trial user paused (waiting for selective devices)
  FREEMIUM = "freemium", //freemium user with just two devices (has access to all features)
  ACTIVE_CANCELED = "active_canceled",
}

export interface TCurrentPlan {
  id: string;
  stripeSubscriptionId: string;
  priceId: string;
  expiresIn: string; // ISO string format
  startsIn: string; // ISO string format
  status: SubscriptionStateEnum;
  devicesCount: number;
  employeeId: string;
  price: {
    id: string;
    stripePriceId: string;
    currency: string;
    tiers: {
      min: number;
      max: number;
      amount: number;
    }[];
    type: string;
    period: PeriodsTypeEnums;
    product: {
      id: string;
      name: string;
      bundle: PlansTypeEnums;
      trialDays: number;
      discountPercentage: number;
      platform: EPlatform;
    };
  };
}
export interface TPaymentPlan {
  billingDetails: {
    address: {
      country: string;
    };
    email: string;
    name: string;
  };
  card: {
    brand: string;
    country: string;
    fingerprint: string;
    funding: string;
    last4: string;
  };

  id: string;
  isPrimary: boolean;
  stripePaymentMethodId: string;
  type: string;
}

export interface TPlan {
  stripeCustomer: {
    subscription: TCurrentPlan;
    trialsCount: number;
    paymentMethods?: TPaymentMethod[];
  };
}

export interface TInvoiceList {
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  stripeInvoiceId: string;
  productId: string;
  priceId: string;
  status: string;
  invoiceUrl: string;
  invoiceNumber: string;
  periodStart: string;
  periodEnd: string;
  totalAmount: string;
  currency: string;
  quantity: number;
  statusTransitions: {
    paid_at: string;
    voided_at: string;
    finalized_at: string;
    marked_uncollectible_at: string;
  };
  isRefunded: false;
  organizationId: string;
  companyId: string;
  plan: PlansTypeEnums;
  period: PeriodsTypeEnums;
  organization: {
    id: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    name: string;
    displayName: string;
    region: string;
    logo: string;
    email: string;
    description: string | null;
    country: string;
    industry: string;
    ownerId: string;
    companyId: string;
    url: string;
    mspCompanyId: string;
    createdById: string;
  };
}

export interface TInfo {
  amount: number;
  count: number;
  src: string;
}
